import React, { useState } from 'react';
import { Clock, User, Plus, Minus, ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { cn } from '../../../lib/utils';
import { LineItem } from '../../../models/workItem';

interface LineItemDetailsProps {
  lineItem: LineItem;
}

const LineItemDetails: React.FC<LineItemDetailsProps> = ({ lineItem }) => {
  const [showMaterials, setShowMaterials] = useState(false);
  // Generate a unique ID for each line item based on description
  const lineItemId = `line-item-${lineItem.description.replace(/\s+/g, '-').toLowerCase()}`;

  const toggleMaterials = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMaterials((prev) => !prev);
  };

  const calculateSubTaskCosts = () => {
    return lineItem.analysis.subTasks.reduce(
      (acc, subTask) => {
        const laborCosts = subTask.options.reduce(
          (optAcc, option) => ({
            hoursLow: optAcc.hoursLow + option.expectedLaborHoursLow,
            hoursHigh: optAcc.hoursHigh + option.expectedLaborHoursHigh,
            costLow: optAcc.costLow + option.expectedLaborHoursLow * option.laborCostLowPerHour,
            costHigh: optAcc.costHigh + option.expectedLaborHoursHigh * option.laborCostHighPerHour,
            rateLow: Math.min(optAcc.rateLow, option.laborCostLowPerHour),
            rateHigh: Math.max(optAcc.rateHigh, option.laborCostHighPerHour),
          }),
          {
            hoursLow: 0,
            hoursHigh: 0,
            costLow: 0,
            costHigh: 0,
            rateLow: Infinity,
            rateHigh: 0,
          }
        );

        const materialCosts = subTask.options.reduce(
          (optAcc, option) => ({
            costLow:
              optAcc.costLow +
              option.materials.reduce(
                (matAcc, material) => matAcc + material.lowCostPerUnit * material.quantityLow,
                0
              ),
            costHigh:
              optAcc.costHigh +
              option.materials.reduce(
                (matAcc, material) => matAcc + material.highCostPerUnit * material.quantityHigh,
                0
              ),
          }),
          { costLow: 0, costHigh: 0 }
        );

        return {
          hoursLow: acc.hoursLow + laborCosts.hoursLow,
          hoursHigh: acc.hoursHigh + laborCosts.hoursHigh,
          laborCostLow: acc.laborCostLow + laborCosts.costLow,
          laborCostHigh: acc.laborCostHigh + laborCosts.costHigh,
          rateLow: Math.min(acc.rateLow, laborCosts.rateLow),
          rateHigh: Math.max(acc.rateHigh, laborCosts.rateHigh),
          materialCostLow: acc.materialCostLow + materialCosts.costLow,
          materialCostHigh: acc.materialCostHigh + materialCosts.costHigh,
        };
      },
      {
        hoursLow: 0,
        hoursHigh: 0,
        laborCostLow: 0,
        laborCostHigh: 0,
        rateLow: Infinity,
        rateHigh: 0,
        materialCostLow: 0,
        materialCostHigh: 0,
      }
    );
  };

  const {
    hoursLow,
    hoursHigh,
    laborCostLow,
    laborCostHigh,
    rateLow,
    rateHigh,
    materialCostLow,
    materialCostHigh,
  } = calculateSubTaskCosts();

  const totalEstimateLow = laborCostLow + materialCostLow;
  const totalEstimateHigh = laborCostHigh + materialCostHigh;

  const formatDifference = (expected: [number, number], given: number) => {
    if (given === 0) return { difference: 'N/A', status: 'text-slate-500', label: 'Not Given', arrow: null };

    const [low, high] = expected;

    let difference = 0;
    let status = 'text-green-600'; // Green for In Range
    let label = 'In Range';
    let arrow = null;
    let bgColor = 'bg-green-50';
    let borderColor = 'border-green-200';

    if (given > high) {
      difference = ((given - high) / high) * 100;
      status = 'text-red-600'; // Red for Over Range
      label = 'Over Estimate';
      arrow = <ArrowUp className="h-4 w-4" />;
      bgColor = 'bg-red-50';
      borderColor = 'border-red-200';
    } else if (given < low) {
      difference = ((low - given) / low) * 100;
      status = 'text-amber-600'; // Yellow for Under Range
      label = 'Under Estimate';
      arrow = <ArrowDown className="h-4 w-4" />;
      bgColor = 'bg-amber-50';
      borderColor = 'border-amber-200';
    }

    return {
      difference: difference.toFixed(1),
      status,
      label,
      arrow,
      bgColor,
      borderColor
    };
  };

  const formatRange = (low: number, high: number, unit = '', prefix = '') =>
    low === high
      ? `${prefix}${low.toLocaleString()}${unit ? ` ${unit}` : ''}`
      : `${prefix}${low.toLocaleString()} - ${prefix}${high.toLocaleString()}${unit ? ` ${unit}` : ''}`;

  const totalDifference = formatDifference(
    [totalEstimateLow, totalEstimateHigh],
    lineItem.givenTotalCost
  );

  return (
    <Accordion type="single" collapsible className="border rounded-lg overflow-hidden">
      <AccordionItem value="item" className="border-0">
        <AccordionTrigger className="px-4 py-3 hover:no-underline bg-slate-50 hover:bg-slate-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between w-full gap-2">
            <span className="font-medium text-left">
              {lineItem.description}: ${lineItem.givenTotalCost?.toLocaleString() || 'Not Given'}
            </span>
            {totalDifference.difference !== 'N/A' && (
              <Badge 
                variant="outline" 
                className={cn(
                  "flex items-center gap-1 md:ml-auto", 
                  totalDifference.bgColor, 
                  totalDifference.status, 
                  totalDifference.borderColor
                )}
              >
                {totalDifference.arrow}
                <span>
                  {totalDifference.difference !== '0.0' ? `${totalDifference.difference}%` : ''}{' '}
                  {totalDifference.label}
                </span>
              </Badge>
            )}
          </div>
        </AccordionTrigger>
        
        <AccordionContent className="px-4 py-4">
          <div className="space-y-6">
            {/* Fxer Estimate Section */}
            <div>
              <h4 className="font-bold text-base mb-4">Fxer Estimate</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Labor Section */}
                <div className="space-y-2">
                  <h5 className="font-semibold text-sm">Labor</h5>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-slate-600 flex-shrink-0" />
                    <span>{formatRange(Math.ceil(hoursLow), Math.ceil(hoursHigh), 'hours')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-slate-600 flex-shrink-0" />
                    <span>Rate: {formatRange(Math.ceil(rateLow), Math.ceil(rateHigh), '', '$')}/hr</span>
                  </div>
                  <div className="font-semibold text-sm mt-2">
                    Total Labor: {formatRange(laborCostLow, laborCostHigh, '', '$')}
                  </div>
                </div>

                {/* Materials Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h5 className="font-semibold text-sm">Materials</h5>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={toggleMaterials} 
                      className="h-6 w-6 p-0"
                    >
                      {showMaterials ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {showMaterials && (
                    <ul className="space-y-3 mt-2 max-h-48 overflow-y-auto">
                      {lineItem.analysis.subTasks.map((subTask, index) =>
                        subTask.options.flatMap((option, optIndex) =>
                          option.materials.map((material, matIndex) => (
                            <li key={`${index}-${optIndex}-${matIndex}`} className="ml-4 text-sm border-l-2 border-slate-200 pl-3">
                              <span className="font-semibold block">{material.description}</span>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-1">
                                <span>Quantity: {formatRange(material.quantityLow, material.quantityHigh)} {material.unit}</span>
                                <span>Unit Price: {formatRange(material.lowCostPerUnit, material.highCostPerUnit, '', '$')}</span>
                                <span className="col-span-2">Total: {formatRange(material.lowCostPerUnit * material.quantityLow, material.highCostPerUnit * material.quantityHigh, '', '$')}</span>
                              </div>
                            </li>
                          ))
                        )
                      )}
                    </ul>
                  )}
                  
                  <div className="font-semibold text-sm mt-2">
                    Total Materials: {formatRange(materialCostLow, materialCostHigh, '', '$')}
                  </div>
                </div>
              </div>

              {/* Total Estimate */}
              <div className="font-bold mt-4 text-base">
                Total Estimate: {formatRange(totalEstimateLow, totalEstimateHigh, '', '$')}
              </div>
            </div>

            {/* Quote Breakdown */}
            <div className="border-t border-slate-200 pt-4">
              <h4 className="font-bold text-base mb-4">Quote Breakdown</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-semibold text-sm">Labor</h5>
                  <p className="text-sm mt-1">
                    ${lineItem.givenLaborCost?.toLocaleString() || 'Not Given'}
                  </p>
                </div>
                <div>
                  <h5 className="font-semibold text-sm">Material</h5>
                  <p className="text-sm mt-1">
                    ${lineItem.givenMaterialCost?.toLocaleString() || 'Not Given'}
                  </p>
                </div>
              </div>
              <div className="font-bold mt-4">
                Total: ${lineItem.givenTotalCost?.toLocaleString() || 'Not Given'}
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default LineItemDetails;